import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { AppIconComponent } from '../../../lib/ui';
import {
  ProfileViewService,
  UserRole,
  ProfileViewConfig,
} from '../../../core/profile-view.service';

@Component({
  selector: 'app-profile-selector',
  standalone: true,
  imports: [CommonModule, AppIconComponent],
  templateUrl: './profile-selector.component.html',
  styleUrls: ['./profile-selector.component.scss'],
})
export class ProfileSelectorComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  canSwitchProfiles = false;
  isViewModeActive = false;
  currentProfile: ProfileViewConfig | null = null;
  availableProfiles: ProfileViewConfig[] = [];
  isDropdownOpen = false;

  constructor(private profileViewService: ProfileViewService) {}

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: Event) {
    const target = event.target as HTMLElement;
    if (!target.closest('.profile-selector') && this.isDropdownOpen) {
      this.closeDropdown();
    }
  }

  async ngOnInit(): Promise<void> {
    try {
      // Verificar se pode trocar perfis - sempre true para ESTOKAI_OWNER
      this.canSwitchProfiles = true;

      // Obter perfis disponíveis
      this.availableProfiles =
        await this.profileViewService.getAvailableProfiles();

      if (this.availableProfiles.length === 0) {
        console.warn(
          'No available profiles found. Check if the user is ESTOKAI_OWNER'
        );
        return;
      }

      // Observar mudanças no perfil atual
      this.profileViewService
        .getCurrentViewProfile()
        .pipe(takeUntil(this.destroy$))
        .subscribe((role) => {
          this.currentProfile =
            this.availableProfiles.find((p) => p.role === role) || null;
          console.log('Current profile updated:', this.currentProfile);
        });

      // Observar se está em modo visualização
      this.profileViewService
        .getIsViewModeActive()
        .pipe(takeUntil(this.destroy$))
        .subscribe((isActive) => {
          this.isViewModeActive = isActive;
          console.log('View mode active:', isActive);
        });
    } catch (error) {
      console.error('Error initializing profile selector:', error);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async switchProfile(targetRole: UserRole): Promise<void> {
    try {
      console.log('Switching to profile:', targetRole);
      const success = await this.profileViewService.switchProfileView(
        targetRole
      );

      if (success) {
        console.log('Successfully switched to profile:', targetRole);
        this.isDropdownOpen = false;
      } else {
        console.warn('Failed to switch profile');
      }
    } catch (error) {
      console.error('Error switching profile:', error);
    }
  }

  async resetToOriginalView(): Promise<void> {
    try {
      await this.profileViewService.resetToOriginalView();
      this.isDropdownOpen = false;
    } catch (error) {
      console.error('Error resetting to original view:', error);
    }
  }

  toggleDropdown(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  closeDropdown(): void {
    this.isDropdownOpen = false;
  }

  getRoleIcon(role: UserRole): string {
    const icons = {
      ESTOKAI_OWNER: 'admin_panel_settings',
      dono: 'business',
      gerente: 'manage_accounts',
      vendedor: 'person',
      owner: 'business',
      financial_manager: 'account_balance',
      sales_user: 'point_of_sale',
      stock_manager: 'inventory',
      partner: 'group',
    };
    return icons[role] || 'person';
  }
}
